import { Input } from '@hud/web-components/Input/Input';
import React, { useEffect, useState } from 'react';
import { StepStyles as Styled } from './SetupSteps.styles';
import { Button } from '@hud/web-components/Button/Button';
import styled from 'styled-components';
import { themeColor } from '@hud/web-components/theme/colors';
import { smBold, xsRegular } from '@hud/web-components/theme/typography';
import { useClient } from '../../../hooks/useClient';
import useSetupCompany from '../../../hooks/useSetupCompany';
import useAnalytics from '../../../hooks/useAnalytics';
import SingleSelectOptionsList from '../../../components/SingleSelectOptionsList/SingleSelectOptionsList';
import { HowDidYouHearAboutUsOption, Tenancy} from '@hud/portal-dtos/webapp-common.dto';


export type CompanyPreset = {
  name: string;
  domain: string;
  tenancy?: Tenancy;
};

interface CreateAccountStepProps {
  initialCompany?: CompanyPreset;
  onSave: () => void;
}

const HowDidYouHearAboutUsLabelToOption: Record<string, HowDidYouHearAboutUsOption> = {
  'Social Network': 'social_network',
  'Community / Forum': 'community_or_forum',
  'Event / Conference': 'event',
  'Search Engine': 'search_engine',
  'Podcast / Webinar': 'podcast_or_webinar',
  Referral: 'referral',
  'Media / Publication': 'media',
  Other: 'other',
};
type HowDidYouHearAboutUsLabel = keyof typeof HowDidYouHearAboutUsLabelToOption;

export function CreateAccountStep({ initialCompany, onSave }: CreateAccountStepProps) {
  const analytics = useAnalytics();
  const isSingleTenant = initialCompany?.tenancy === Tenancy.enum.single;
  const [companyName, setCompanyName] = useState('');
  const [selectedHowDidYouHearAboutUs, setSelectedHowDidYouHearAboutUs] =
    useState<HowDidYouHearAboutUsOption | null>(null);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { isReady } = useClient();
  const updateCompanyName = useSetupCompany();
  const setCompanyNameText = `Set your ${isSingleTenant ? 'workspace' : 'company'} name:`;
  const placeholder = isSingleTenant ? 'Workspace name' : companyName || 'Company name';

  //track event verify_company_page_viewed
  useEffect(() => {
    analytics.capture('verify_company_page_viewed');
  }, []);

  // the account info takes time to load, so we start with empty company name and set it when the account info is loaded if multi-tenant
  useEffect(() => {
    if (typeof initialCompany?.tenancy !== 'undefined' && initialCompany?.tenancy === Tenancy.enum.multi) {
      setCompanyName(initialCompany?.name || initialCompany?.domain?.split('.')[0] || '');
    }
  }, [initialCompany?.tenancy]);

  const handleSubmit = async () => {
    const cleanCompanyName = companyName.trim().replace(/['"]/g, '');
    if (cleanCompanyName === '') {
      setError('Name cannot be empty');
      return;
    }
    if (cleanCompanyName.length < 2) {
      setError('Name must be at least 2 characters');
      return;
    }
    if (cleanCompanyName.length > 50) {
      setError('Name cannot be longer than 50 characters');
      return;
    }
    if (!/^[a-zA-Z0-9\s._-]+$/.test(cleanCompanyName)) {
      setError('Please remove special characters or emojis from the name');
      return;
    }

    try {
      setIsSubmitting(true);
      await updateCompanyName.mutateAsync({
        companyName: cleanCompanyName,
        howDidYouHearAboutUs: selectedHowDidYouHearAboutUs || undefined,
      });
      analytics.capture('company_name_updated', {
        default_company_name: companyName,
        company_name: cleanCompanyName,
      });
      onSave();
    } catch (err) {
      console.error('Failed to update company name:', err);
      setError('Failed to update company name. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleHowDidYouHearAboutUsOptionSelected = (
    option: HowDidYouHearAboutUsLabel,
  ) => {
    // selected option is the string, we need to convert it to the enum value
    const selectedOption = HowDidYouHearAboutUsLabelToOption[option];
    setSelectedHowDidYouHearAboutUs(selectedOption);
    analytics.capture('how_did_you_hear_about_us_option_selected', {
      how_did_you_hear_about_us_option: selectedOption,
    });
  };

  return (
    <Styled.Container>
      <Styled.Subtitle>Step 1</Styled.Subtitle>
      <Styled.Title>Create account</Styled.Title>

      <Styled.Body>
        <Section>
          <Info>{setCompanyNameText}</Info>
          <StyledInput
            variant="fancy"
            placeholder={placeholder}
            value={companyName}
            onChange={(e) => {
              setCompanyName(e.target.value);
              setError('');
            }}
          />
          {error && <Error>{error}</Error>}
        </Section>

        <Section>
          <Info>How did you hear about us?</Info>
          <SingleSelectOptionsList
            options={Object.keys(HowDidYouHearAboutUsLabelToOption)}
            onChange={handleHowDidYouHearAboutUsOptionSelected}
          />
        </Section>
      </Styled.Body>

      <Styled.Footer>
        <Spacer />
        <StyledButton onClick={handleSubmit} disabled={isSubmitting || !isReady}>
          {isSubmitting ? 'Saving...' : 'Save and Continue'}
        </StyledButton>
      </Styled.Footer>
    </Styled.Container>
  );
}

const Section = styled.div`
  &:not(:last-child) {
    margin-bottom: 24px;
  }
`;

const Info = styled(Styled.Info)`
  margin-bottom: 16px;
`;

const StyledInput = styled(Input)`
  width: 300px;
`;

const StyledButton = styled(Button).attrs({
  variant: 'glow',
})`
  padding: 12px 20px;
  ${smBold};
`;

const Spacer = styled.div`
  flex: 1;
`;

const Error = styled(Styled.Info)`
  ${xsRegular};
  color: ${themeColor('red')};

  margin-top: 6px;
  margin-bottom: 12px;
`;
